import { authService } from '@services/firebase/auth';
import { signInWithGoogle, signOut } from '@utils/auth';
import type { User } from 'firebase/auth';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  initAuth: () => () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null,

        setUser: (user) =>
          set({
            user,
            isAuthenticated: user !== null,
            isLoading: false,
            error: null,
          }),

        setLoading: (loading) => set({ isLoading: loading }),

        setError: (error) => set({ error, isLoading: false }),

        signInWithGoogle: async () => {
          try {
            set({ isLoading: true, error: null });
            const result = await signInWithGoogle();
            set({
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to sign in with Google';
            set({
              error: errorMessage,
              isLoading: false,
              isAuthenticated: false,
              user: null,
            });
            throw error;
          }
        },

        signOut: async () => {
          try {
            set({ isLoading: true, error: null });
            await signOut();
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to sign out';
            set({ error: errorMessage, isLoading: false });
            throw error;
          }
        },

        initAuth: () => {
          // Subscribe to auth state changes
          return authService.onAuthStateChange((user) => {
            get().setUser(user);
          });
        },
      }),
      { name: 'auth-storage' }
    ),
    { name: 'AuthStore' }
  )
);
